/**
 * Client typé pour les routes `/events` — Phase 4, incrément 4.6,
 * étendu **offline-first** en Phase 3.3.2.
 *
 * Les **mutations** (`createEvent`, `updateEvent`, `deleteEvent`)
 * passent désormais par `sendOrQueueMutation` (cf. 3.3.1) qui
 * enqueue automatiquement la mutation en IDB quand le réseau est
 * indisponible. Le retour est un `SendResult<T>` discriminant :
 *
 *   - `{kind: 'sent', data}` — la requête a réussi côté serveur.
 *   - `{kind: 'queued', seq}` — réseau KO, mutation persistée pour
 *     replay au prochain `online` event. Le caller met à jour son
 *     state local de façon optimiste.
 *
 * La **lecture** (`listEvents`) reste un `apiRequest` direct — pas
 * d'enjeu offline, l'UI utilise les données locales en attendant.
 *
 * Pattern reproductible pour tasks/messages/journal/etc. en 3.3.3+.
 */
import { apiRequest } from './client.js';
import { sendOrQueueMutation, type SendResult } from './mutationsReplay.js';

/**
 * Event tel que retourné par l'API.
 *
 * `homeId` exposé pour permettre au client de regrouper events par
 * foyer si l'UI affiche un fil cross-foyer (pas utilisé en 4.6 mais
 * pas coûteux à transporter).
 *
 * `createdBy` = id du user qui a créé l'event, `null` si ce user a
 * supprimé son compte depuis (FK ON DELETE SET NULL côté DB).
 */
export interface ApiEvent {
  id: string;
  homeId: string;
  title: string;
  /** Format `YYYY-MM-DD`. */
  date: string;
  /** Format `HH:MM` 24h, ou `null` si non précisé. */
  time: string | null;
  location: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateEventInput {
  title: string;
  /** Format `YYYY-MM-DD`. */
  date: string;
  /** Format `HH:MM` 24h. */
  time?: string;
  location?: string;
}

/**
 * Update partiel. Au moins un champ doit être présent (validé
 * côté serveur via Zod refine). `null` accepté sur `time` et
 * `location` pour reset, mais pas sur `title` ni `date` (champs
 * obligatoires).
 */
export interface UpdateEventInput {
  title?: string;
  date?: string;
  time?: string | null;
  location?: string | null;
}

/**
 * Liste les events d'un foyer. Throw `ApiError` 403 si pas membre.
 * **Pas offline-first** — la lecture utilise les données locales
 * en mode dégradé.
 */
export async function listEvents(homeId: string): Promise<ApiEvent[]> {
  const data = await apiRequest<{ events: ApiEvent[] }>(`/homes/${homeId}/events`);
  return data.events;
}

/**
 * Crée un event dans un foyer. **Offline-first** via
 * `sendOrQueueMutation` :
 *
 *   - Online + 201 → `{kind: 'sent', data: { event }}`. Si
 *     `localId` est fourni, le replay engine émet un
 *     `nestor:mutation-remap` après le succès pour mapper
 *     `localId → serverId` côté store domain (évite le doublon
 *     visuel au prochain GET refresh — Phase 3.3.7).
 *   - Offline ou network failure → `{kind: 'queued', seq}` ; le
 *     caller garde le local id pour le retry au prochain replay.
 *     Le `localId` est persisté dans `MutationEntry` pour le
 *     remap au replay.
 *   - `ApiError` (400/403/etc.) → throw (le serveur a tranché).
 *
 * Tout membre peut créer (parent / teen / child) — pas de
 * restriction de rôle.
 */
export async function createEvent(
  homeId: string,
  input: CreateEventInput,
  localId?: string,
): Promise<SendResult<{ event: ApiEvent }>> {
  return sendOrQueueMutation<{ event: ApiEvent }>({
    method: 'POST',
    path: `/homes/${homeId}/events`,
    body: input,
    ...(localId ? { localId } : {}),
  });
}

/**
 * Met à jour title / date / time / location d'un event.
 * **Offline-first**. Tout membre du foyer peut modifier. `ApiError`
 * 403 si pas membre, ou si event inexistant (no-leak).
 */
export async function updateEvent(
  id: string,
  input: UpdateEventInput,
): Promise<SendResult<{ event: ApiEvent }>> {
  return sendOrQueueMutation<{ event: ApiEvent }>({
    method: 'PATCH',
    path: `/events/${id}`,
    body: input,
  });
}

/**
 * Supprime un event. **Offline-first**. Retourne `kind: 'sent'`
 * avec `data: undefined` sur 204 No Content (cohérent avec
 * `apiRequest` qui rend `undefined as T` sur 204).
 */
export async function deleteEvent(id: string): Promise<SendResult<void>> {
  return sendOrQueueMutation<void>({
    method: 'DELETE',
    path: `/events/${id}`,
  });
}
